import * as i0 from '@angular/core';
import { Component, Inject, NgModule, Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, of, Observable, throwError, pipe, catchError as catchError$1 } from 'rxjs';
import * as i1 from '@angular/common/http';
import { HttpParams, HttpResponse, HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { filter, mergeMap, catchError, tap, map, first, share, switchMap } from 'rxjs/operators';
import * as i2$1 from '@uofu-uss/ngx-auth';
import { LoggedInStatus } from '@uofu-uss/ngx-auth';
import * as i2 from '@uofu-uss/angular-util';
import { NotificationService } from '@uofu-uss/angular-util';
import { cloneDeep, unionWith, compact, sortBy } from 'lodash-es';
import { addDays, isAfter, parseISO } from 'date-fns';

class SkipLinkComponent {
    constructor(document) {
        this.document = document;
    }
    skipToMainContent() {
        this.document.getElementById('uu-skip-target')?.focus();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: SkipLinkComponent, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.3", type: SkipLinkComponent, isStandalone: true, selector: "sc-skip-link", ngImport: i0, template: "<div id=\"skipLink\">\n  <button (click)=\"skipToMainContent()\" class=\"btn btn-light\">Skip to main content</button>\n</div>\n", styles: ["#skipLink button:focus{top:0;transition:top .1s ease-in 0s}#skipLink button{position:absolute;z-index:9000;margin-top:0;padding:.5em 1em;top:-3em;transition:top .5s ease-out .5s}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: SkipLinkComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sc-skip-link', standalone: true, template: "<div id=\"skipLink\">\n  <button (click)=\"skipToMainContent()\" class=\"btn btn-light\">Skip to main content</button>\n</div>\n", styles: ["#skipLink button:focus{top:0;transition:top .1s ease-in 0s}#skipLink button{position:absolute;z-index:9000;margin-top:0;padding:.5em 1em;top:-3em;transition:top .5s ease-out .5s}\n"] }]
        }], ctorParameters: () => [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class ScCoreModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: ScCoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.3", ngImport: i0, type: ScCoreModule, imports: [SkipLinkComponent], exports: [SkipLinkComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: ScCoreModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: ScCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        SkipLinkComponent,
                    ],
                    exports: [
                        SkipLinkComponent,
                    ],
                }]
        }] });

class BehaviorSubjectService {
    constructor(initialValue) {
        this.myValue = initialValue;
        this.myObservable = new BehaviorSubject(initialValue);
    }
    get value() {
        return this.myValue;
    }
    set value(given) {
        this.myValue = given;
        this.myObservable.next(this.myValue);
    }
    get observable() {
        return this.myObservable;
    }
}

class CacheService {
    // 5 minutes. Things are only good in the cache for this amount of time.
    static { this.timeToLive = 5 * 60 * 1000; }
    #cachedData = {};
    constructor() {
    }
    getCachedData(url) {
        const result = this.#cachedData[url];
        return result ? this.getOrRemoveExpired(url, result) : undefined;
    }
    getOrRemoveExpired(url, value) {
        const now = new Date().getTime();
        const diff = now - value.when;
        if (diff > CacheService.timeToLive) {
            this.clearFromCache(url);
            return undefined;
        }
        return value.value;
    }
    setCachedData(url, data) {
        this.#cachedData[url] = {
            value: data,
            when: new Date().getTime(),
        };
    }
    clearFromCache(url) {
        return delete this.#cachedData[url];
    }
    clearCache() {
        this.#cachedData = {};
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: CacheService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: CacheService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: CacheService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class CacheRegistrationService {
    #services = new Set();
    addedToCache(serviceUri) {
        return !![...this.#services].find(current => serviceUri.startsWith(current));
    }
    addToCache(serviceUri) {
        this.#services.add(serviceUri);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: CacheRegistrationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: CacheRegistrationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: CacheRegistrationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class DownloadDataService {
    static { this.userDownloadUrl = '/api/user-download'; }
    static { this.fileUrl = '/api/download-file'; }
    static { this.folderUrl = '/api/download-folder'; }
    constructor(http) {
        this.http = http;
    }
    fetchAvailableByUnid(unid) {
        return this.http.get(`${DownloadDataService.userDownloadUrl}/unid/${unid}`);
    }
    fetchAvailableByCart(id) {
        return this.http.get(`${DownloadDataService.userDownloadUrl}/cart/${id}`);
    }
    fetchDownloadsByOffering(id) {
        return this.http.get(`${DownloadDataService.userDownloadUrl}/offering/${id}`);
    }
    fetchDownloadUrl(fileId, redirect = true) {
        return this.http.get(`${DownloadDataService.fileUrl}/generate-download-url/${fileId}?redirect=${redirect}`, { responseType: 'text' });
    }
    fetchDownloadsByFolderId(folderId) {
        return this.http.get(`${DownloadDataService.fileUrl}/${folderId}`);
    }
    fetchDownloadsByFolderName(folderName) {
        return this.http.get(DownloadDataService.folderUrl, { params: new HttpParams().append('name', folderName) });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: DownloadDataService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: DownloadDataService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: DownloadDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }] });

var DownloadType;
(function (DownloadType) {
    DownloadType["INSTALLER"] = "INSTALLER";
    DownloadType["LICENSE"] = "LICENSE";
    DownloadType["INSTRUCTION"] = "INSTRUCTION";
    DownloadType["UNKNOWN"] = "UNKNOWN";
})(DownloadType || (DownloadType = {}));
class DownloadService {
    constructor(downloadDataService) {
        this.downloadDataService = downloadDataService;
    }
    fetchDownloadUrl(fileId) {
        return this.downloadDataService.fetchDownloadUrl(fileId, false);
    }
    /**
     * Fetch the download information by offeringId.
     * The user must be an admin or have purchased the offering
     */
    fetchDownloadsByOffering(offeringId) {
        return this.downloadDataService.fetchDownloadsByOffering(offeringId);
    }
    /** Fetch the files in the given folder */
    fetchDownloadsByFolderId(folderId) {
        return this.downloadDataService.fetchDownloadsByFolderId(folderId);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: DownloadService, deps: [{ token: DownloadDataService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: DownloadService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: DownloadService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: DownloadDataService }] });

class UserDataService {
    static { this.apiUrl = '/api/user'; }
    constructor(http, cacheRegistrationService) {
        this.http = http;
        this.cacheRegistrationService = cacheRegistrationService;
        cacheRegistrationService.addToCache(UserDataService.apiUrl);
    }
    fetchLoggedInUser() {
        return this.http.get(`${UserDataService.apiUrl}/current`);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: UserDataService, deps: [{ token: i1.HttpClient }, { token: CacheRegistrationService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: UserDataService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: UserDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }, { type: CacheRegistrationService }] });

class UserService extends BehaviorSubjectService {
    static { this.emptyUser = {
        name: '',
        unid: '',
        eligible: false,
        admin: false,
        student: false,
        employee: false,
        deptId: '',
        emeritus: false,
    }; }
    constructor(userDataService) {
        super(UserService.emptyUser);
        this.userDataService = userDataService;
    }
    isAdmin(user = this.value) {
        return user && user.admin;
    }
    isUserDefined(user = this.value) {
        return user && user !== UserService.emptyUser;
    }
    fetchLoggedInUser() {
        return this.userDataService.fetchLoggedInUser();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: UserService, deps: [{ token: UserDataService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: UserService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: UserService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: UserDataService }] });

class AdminGuard {
    constructor(userService, notificationService, loginService) {
        this.userService = userService;
        this.notificationService = notificationService;
        this.loginService = loginService;
    }
    canActivate(next, state) {
        return this.loginService.loggedInStatusObservable
            .pipe(filter((ls) => ls !== LoggedInStatus.Unknown), mergeMap((ls) => (ls === LoggedInStatus.LoggedIn) ? this.userService.fetchLoggedInUser() : of(UserService.emptyUser)), catchError(() => of(UserService.emptyUser)), tap((user) => this.handleFetchUserSideEffect(user)), map((user) => user.admin), first());
    }
    handleFetchUserSideEffect(user) {
        if (this.userService.isUserDefined(user) && !user.admin) {
            this.notificationService.addWarning('Not an admin user, access restricted.');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: AdminGuard, deps: [{ token: UserService }, { token: i2.NotificationService }, { token: i2$1.LoginService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: AdminGuard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: AdminGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: UserService }, { type: i2.NotificationService }, { type: i2$1.LoginService }] });

class LegacyRedirectGuard {
    constructor(userService, loginService, document) {
        this.userService = userService;
        this.loginService = loginService;
        this.document = document;
    }
    canActivate(next, state) {
        return this.loginService.loggedInStatusObservable
            .pipe(filter((ls) => ls !== LoggedInStatus.Unknown), mergeMap((ls) => (ls === LoggedInStatus.LoggedIn) ? this.userService.fetchLoggedInUser() : of(UserService.emptyUser)), catchError(() => of(UserService.emptyUser)), tap((user) => this.handleFetchUserSideEffect(user)), map((user) => user && user.eligible), first());
    }
    handleFetchUserSideEffect(user) {
        if (this.userService.isUserDefined(user) && !user.eligible) {
            // redirect to old store
            const location = this.document.location.href;
            if (location.indexOf('app.dev') >= 0) {
                this.document.location.href = 'https://dev.sys.utah.edu/uofu/misc/osl/index.shop';
            }
            else if (location.indexOf('app.test') >= 0) {
                this.document.location.href = 'https://test.sys.utah.edu/uofu/misc/osl/index.shop';
            }
            else {
                this.document.location.href = 'https://software-store.utah.edu/uofu/misc/osl/index.shop';
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: LegacyRedirectGuard, deps: [{ token: UserService }, { token: i2$1.LoginService }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: LegacyRedirectGuard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: LegacyRedirectGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: UserService }, { type: i2$1.LoginService }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class LoadUserGuard {
    constructor(userService, loginService) {
        this.userService = userService;
        this.loginService = loginService;
    }
    canActivate(next, state) {
        return this.loginService.loggedInStatusObservable
            .pipe(filter((ls) => ls !== LoggedInStatus.Unknown), mergeMap((ls) => (ls === LoggedInStatus.LoggedIn) ? this.userService.fetchLoggedInUser() : of(UserService.emptyUser)), catchError((e) => {
            console.error(`unable to fetch user: ${e}`);
            return of(UserService.emptyUser);
        }), tap(user => this.handleFetchUserSideEffect(user)), map((user) => this.userService.isUserDefined(user)), first());
    }
    handleFetchUserSideEffect(user) {
        if (this.userService.isUserDefined(user)) {
            this.userService.value = user;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: LoadUserGuard, deps: [{ token: UserService }, { token: i2$1.LoginService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: LoadUserGuard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: LoadUserGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: UserService }, { type: i2$1.LoginService }] });

/*
 * Thanks to https://medium.com/@vikeshm/data-caching-angular-http-interceptor-2d87f95e2340
 */
class CacheInterceptor {
    constructor(cacheRegistrationService, cacheService) {
        this.cacheRegistrationService = cacheRegistrationService;
        this.cacheService = cacheService;
    }
    intercept(httpRequest, handler) {
        // Clear entire cache on any PUT, POST, or DELETE request
        if (httpRequest.method === 'PUT' || httpRequest.method === 'POST' || httpRequest.method === 'DELETE') {
            console.info(`clearing entire cache upon ${httpRequest.method}: ${httpRequest.url}`);
            this.cacheService.clearCache();
        }
        // Don't cache if
        // 1. It's not a GET request
        // 2. If URI is not supposed to be cached
        if (httpRequest.method !== 'GET' || !this.cacheRegistrationService.addedToCache(httpRequest.url)) {
            console.info(`skipping cache for ${httpRequest.method}: ${httpRequest.url}`);
            return handler.handle(httpRequest);
        }
        // Checked if there is cached data for this URI
        const lastResponse = this.cacheService.getCachedData(httpRequest.urlWithParams);
        if (lastResponse) {
            // In case of parallel requests to same URI, return the request already in progress
            // otherwise return the last cached data
            console.info(`returning cached response for ${httpRequest.urlWithParams}`);
            return (lastResponse instanceof Observable) ? lastResponse : of(this.cloneResponse(lastResponse));
        }
        else {
            // If the request of going through for first time then let the request proceed and cache the response
            const requestHandle = this.makeRequest(httpRequest, handler);
            // Meanwhile cache the request Observable to handle parallel request
            console.info(`returning observable for ${httpRequest.urlWithParams}`);
            this.cacheService.setCachedData(httpRequest.urlWithParams, requestHandle);
            return requestHandle;
        }
    }
    cloneResponse(response) {
        return response.clone({ body: cloneDeep(response.body) });
    }
    makeRequest(httpRequest, handler) {
        return handler.handle(httpRequest)
            .pipe(tap((stateEvent) => {
            if (stateEvent instanceof HttpResponse) {
                console.info(`caching response for ${httpRequest.urlWithParams}`);
                // always set a clone of the value.
                this.cacheService.setCachedData(httpRequest.urlWithParams, this.cloneResponse(stateEvent));
            }
        }), catchError((e) => {
            this.cacheService.clearFromCache(httpRequest.urlWithParams);
            return throwError(e);
        }), share());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: CacheInterceptor, deps: [{ token: CacheRegistrationService }, { token: CacheService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: CacheInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: CacheInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: CacheRegistrationService }, { type: CacheService }] });

class LicenseDataService {
    constructor() {
        this.http = inject(HttpClient);
    }
    static { this.baseUrl = '/api/product-license'; }
    fetchLicense(id) {
        return this.http.get(`${LicenseDataService.baseUrl}/${id}`);
    }
    createLicense(license) {
        return this.http.put(`${LicenseDataService.baseUrl}`, license);
    }
    updateLicense(license) {
        return this.http.post(`${LicenseDataService.baseUrl}/${license.id}`, license);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: LicenseDataService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: LicenseDataService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: LicenseDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class LicenseService {
    constructor() {
        this.dataService = inject(LicenseDataService);
    }
    fetchLicense(id) {
        return this.dataService.fetchLicense(id);
    }
    createLicense(license) {
        return this.dataService.createLicense(license);
    }
    updateLicense(license) {
        return this.dataService.updateLicense(license);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: LicenseService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: LicenseService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: LicenseService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class Messages {
    static { this.CONTACT_MSG = 'Please contact support if the problem continues.'; }
    static { this.LOGIN_MSG = `Unable to initiate sign in. ${Messages.CONTACT_MSG}`; }
    static { this.LOAD_CART_MSG = `Unable to load your shopping cart. ${Messages.CONTACT_MSG}`; }
    static { this.LOAD_PROD_MSG = 'Unable to load product'; }
    static validationMsg(errorResponse, defaultMsg) {
        const message = errorResponse?.headers?.get('sc-status-message');
        return errorResponse?.status === 400 && message ? message : defaultMsg;
    }
}

class OfferingDataService {
    static { this.baseUrl = '/api/product-offering'; }
    constructor(http) {
        this.http = http;
    }
    fetch(id) {
        return this.http.get(`${OfferingDataService.baseUrl}/${id}`);
    }
    createOffering(offering) {
        return this.http.put(`${OfferingDataService.baseUrl}`, offering);
    }
    updateOffering(offering) {
        return this.http.post(`${OfferingDataService.baseUrl}/${offering.id}`, offering);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: OfferingDataService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: OfferingDataService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: OfferingDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }] });

var LicenseType;
(function (LicenseType) {
    LicenseType["Student"] = "STUDENT";
    LicenseType["University"] = "UNIVERSITY";
    LicenseType["Employee"] = "EMPLOYEE";
})(LicenseType || (LicenseType = {}));
var Status;
(function (Status) {
    Status["Active"] = "ACTIVE";
    Status["Inactive"] = "INACTIVE";
})(Status || (Status = {}));
var ProductOfferingStatus;
(function (ProductOfferingStatus) {
    ProductOfferingStatus["Active"] = "ACTIVE";
    ProductOfferingStatus["Inactive"] = "INACTIVE";
    ProductOfferingStatus["Pending"] = "PENDING";
})(ProductOfferingStatus || (ProductOfferingStatus = {}));
var UpgradeProtectionType;
(function (UpgradeProtectionType) {
    UpgradeProtectionType["Included"] = "INCLUDES_UPGRADE_PROTECTION";
    UpgradeProtectionType["UpgradeProtectionOnly"] = "UPGRADE_PROTECTION_ONLY";
})(UpgradeProtectionType || (UpgradeProtectionType = {}));
var UsageType;
(function (UsageType) {
    UsageType["SingleUser"] = "SINGLE_USER";
    UsageType["NetworkServer"] = "NETWORK_SERVER";
    UsageType["NodeClient"] = "NODE_CLIENT";
    UsageType["ConcurrentUser"] = "CONCURRENT_USER";
    UsageType["Floating"] = "FLOATING";
    UsageType["FullTimeEquivalent"] = "FULL_TIME_EQUIVALENT";
})(UsageType || (UsageType = {}));
var ContractTerm;
(function (ContractTerm) {
    ContractTerm["PerpetualLicense"] = "PERPETUAL_LICENSE";
    ContractTerm["AnnualLicense"] = "ANNUAL_LICENSE";
    ContractTerm["OneYearTerm"] = "ONE_YEAR_TERM";
    ContractTerm["TwoYearTerm"] = "TWO_YEAR_TERM";
    ContractTerm["ThreeYearTerm"] = "THREE_YEAR_TERM";
})(ContractTerm || (ContractTerm = {}));
var MediaType;
(function (MediaType) {
    MediaType["CD"] = "CD";
    MediaType["DVD"] = "DVD";
    MediaType["Download"] = "DOWNLOAD";
    MediaType["NoMedia"] = "NO_MEDIA";
    MediaType["CDBackup"] = "CD_BACKUP";
    MediaType["DVDBackup"] = "DVD_BACKUP";
    MediaType["DownloadOnly"] = "DOWNLOAD_ONLY";
})(MediaType || (MediaType = {}));
var WorkAtHomeType;
(function (WorkAtHomeType) {
    WorkAtHomeType["Included"] = "INCLUDED";
    WorkAtHomeType["NotIncluded"] = "NOT_INCLUDED";
})(WorkAtHomeType || (WorkAtHomeType = {}));

function generateEmptyProductOffering() {
    return {
        id: 0,
        productLicenseId: 0,
        platform: generateEmptyPlatform(),
        price: 0,
        version: '',
        startDate: '',
        endDate: '',
        status: ProductOfferingStatus.Inactive,
        usageType: UsageType.SingleUser,
        media: MediaType.NoMedia,
        serialNumber: '',
        downloadAvailableWithoutPurchase: false,
        installDirections: '',
        workAtHome: WorkAtHomeType.NotIncluded,
    };
}
function generateEmptyPlatform() {
    return {
        id: 0,
        title: '',
    };
}
class OfferingService {
    constructor(offeringDataService) {
        this.offeringDataService = offeringDataService;
    }
    fetchOffering(id) {
        return this.offeringDataService.fetch(id);
    }
    createOffering(offering) {
        return this.offeringDataService.createOffering(offering);
    }
    updateOffering(offering) {
        return this.offeringDataService.updateOffering(offering);
    }
    expired(endDate) {
        return endDate ? this.expiresWithin(endDate, 0) : false;
    }
    /**
     * Determine the number of days until a license is expired.
     * The license (as has been coded for 1 year+) expires at the start of the end date.
     * Example 1:
     * Today is Jan 1 2022 and EndDate is Jan 3 2022
     * Then expires within 1 should be false.
     * Then expires within 2 should be true.
     * Then expires within 3 should be true.
     *
     * Example 2:
     * Today is Jan 1 2022 and EndDate is Jan 1 2022
     * Then expires within 1 should be true.
     * Then expires within 0 should be true.
     *
     * Note: DateService was added as parsing dates from string with new Date() is
     * discouraged.
     */
    expiresWithin(endDate, days) {
        if (endDate) {
            const when = addDays(new Date(), days);
            return !isAfter(endDate, when);
        }
        return false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: OfferingService, deps: [{ token: OfferingDataService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: OfferingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: OfferingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: OfferingDataService }] });

class OfferingOperatorService {
    constructor() {
        this.notificationService = inject(NotificationService);
        this.offeringService = inject(OfferingService);
    }
    fetchOffering() {
        return pipe(switchMap(id => !id
            ? of(generateEmptyProductOffering())
            : this.offeringService.fetchOffering(id)
                .pipe(catchError(err => {
                if (err instanceof HttpErrorResponse && err.status === HttpStatusCode.NotFound) {
                    this.notificationService.addError(`A product offering with id: ${id} could not be found`);
                }
                else { /* Unexpected Error */
                    this.notificationService.addError(`Failed to load product offering: ${err.message || 'Please contact support if the problem continues'}`);
                }
                return of(generateEmptyProductOffering());
            }))));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: OfferingOperatorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: OfferingOperatorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: OfferingOperatorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

function AddOffer(Base) {
    return class extends Base {
        // Mixins may not declare private/protected properties
        // however, you can use ES2020 private fields
        #offer;
        #startDate;
        #endDate;
        get offer() {
            return this.#offer;
        }
        set offer(given) {
            this.#offer = given;
            this.#startDate = this.offer?.startDate ? parseISO(this.offer.startDate) : undefined;
            this.#endDate = this.offer?.endDate ? parseISO(this.offer.endDate) : undefined;
        }
        get platform() {
            return this.offer?.platform;
        }
        get price() {
            return this.offer?.price || 0;
        }
        get startDate() {
            return this.#startDate;
        }
        get endDate() {
            return this.#endDate;
        }
        get usageType() {
            return this.offer?.usageType;
        }
        get upgradeProtectionType() {
            return this.offer?.upgradeProtectionType;
        }
        get contractTerm() {
            return this.offer?.contractTerm;
        }
        get version() {
            return this.offer?.version || '';
        }
        get platformTitle() {
            return this.offer?.platform?.title || '';
        }
        get created() {
            return this.offer?.created;
        }
        get updated() {
            return this.offer?.updated;
        }
    };
}

function AddOfferings(Base) {
    return class extends Base {
        // Mixins may not declare private/protected properties
        // however, you can use ES2020 private fields
        #studentOfferings = [];
        #employeeOfferings = [];
        #universityOfferings = [];
        #availablePlatforms = [];
        #studentPlatforms = [];
        #employeePlatforms = [];
        #universityPlatforms = [];
        get studentOfferings() {
            return this.#studentOfferings;
        }
        set studentOfferings(given) {
            this.#studentOfferings = given || [];
            this.#studentPlatforms = this._mapToPlatforms(this.#studentOfferings);
            this._updateAvailablePlatforms();
        }
        get employeeOfferings() {
            return this.#employeeOfferings;
        }
        set employeeOfferings(given) {
            this.#employeeOfferings = given || [];
            this.#employeePlatforms = this._mapToPlatforms(this.#employeeOfferings);
            this._updateAvailablePlatforms();
        }
        get universityOfferings() {
            return this.#universityOfferings;
        }
        set universityOfferings(given) {
            this.#universityOfferings = given || [];
            this.#universityPlatforms = this._mapToPlatforms(this.#universityOfferings);
            this._updateAvailablePlatforms();
        }
        get studentPlatforms() {
            return this.#studentPlatforms;
        }
        get employeePlatforms() {
            return this.#employeePlatforms;
        }
        get universityPlatforms() {
            return this.#universityPlatforms;
        }
        get availablePlatforms() {
            return this.#availablePlatforms;
        }
        get hasStudentOfferings() {
            return this.studentOfferings.length > 0;
        }
        get hasEmployeeOfferings() {
            return this.employeeOfferings.length > 0;
        }
        get hasUniversityOfferings() {
            return this.universityOfferings.length > 0;
        }
        get hasOfferings() {
            return this.hasStudentOfferings || this.hasEmployeeOfferings || this.hasUniversityOfferings;
        }
        getAnyOffering() {
            return this._getAnyOfferingOfType(this.studentOfferings) ||
                this._getAnyOfferingOfType(this.employeeOfferings) ||
                this._getAnyOfferingOfType(this.universityOfferings);
        }
        _updateAvailablePlatforms() {
            const comparator = (a, b) => a.id === b.id;
            this.#availablePlatforms = unionWith(this.studentPlatforms, this.employeePlatforms, this.universityPlatforms, comparator);
        }
        _getAnyOfferingOfType(offerings) {
            const result = offerings && offerings.length && offerings[0];
            return result || null;
        }
        _mapToPlatforms(offerings) {
            return compact(offerings.map(offering => offering.platform));
        }
    };
}

function AddProduct(Base) {
    return class extends Base {
        // Mixins may not declare private/protected properties
        // however, you can use ES2020 private fields
        #product;
        get product() {
            return this.#product;
        }
        set product(given) {
            this.#product = given;
        }
        get id() {
            return this.product?.id || 0;
        }
        get imageId() {
            return this.product?.imageId || 0;
        }
        get name() {
            return this.product?.title || '';
        }
        get featured() {
            return this.product?.featured || false;
        }
        get informationalOnly() {
            return this.product?.informationalOnly || false;
        }
        get informationalUrl() {
            return this.product?.informationalUrl || '';
        }
        get shortDescription() {
            return this.product?.shortDescription || '';
        }
        get longDescription() {
            return this.product?.longDescription || '';
        }
        get faqUrl() {
            return this.product?.faqUrl || '';
        }
        get vendor() {
            return this.product?.vendor;
        }
        get vendorUrl() {
            return this.vendor?.url || '';
        }
        get vendorName() {
            return this.vendor?.fullName || '';
        }
        get created() {
            return this.product?.created;
        }
        get updated() {
            return this.product?.updated;
        }
    };
}

class ProductDataService {
    static { this.baseUrl = '/api/product/'; }
    constructor(http, cacheRegistrationService) {
        this.http = http;
        this.cacheRegistrationService = cacheRegistrationService;
        cacheRegistrationService.addToCache(ProductDataService.baseUrl);
    }
    fetchProducts(filters) {
        const { licenseTypes, productStatus, licenseStatus, offeringStatus, vendors, downloadWithoutPurchase } = filters ?? {};
        // check how spread syntax works on mdn
        let params = new HttpParams().appendAll({
            ...licenseTypes && { licenseTypes: licenseTypes.join(',') },
            ...productStatus && { productStatus },
            ...licenseStatus && { licenseStatus },
            ...offeringStatus && { offeringStatus },
            ...vendors && { vendors: vendors.join(',') },
        });
        if (downloadWithoutPurchase !== undefined) {
            params = params.append('downloadWithoutPurchase', downloadWithoutPurchase);
        }
        return this.http.get(`${ProductDataService.baseUrl}`, { params });
    }
    fetchProduct(id) {
        return this.http.get(`${ProductDataService.baseUrl}${id}`);
    }
    createProduct(product) {
        return this.http.put(`${ProductDataService.baseUrl}`, product);
    }
    updateProduct(product) {
        return this.http.post(`${ProductDataService.baseUrl}${product.id}`, product);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: ProductDataService, deps: [{ token: i1.HttpClient }, { token: CacheRegistrationService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: ProductDataService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: ProductDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }, { type: CacheRegistrationService }] });

class VendorDataService {
    static { this.baseUrl = '/api/vendors/'; }
    constructor(http, cacheRegistrationService) {
        this.http = http;
        this.cacheRegistrationService = cacheRegistrationService;
        cacheRegistrationService.addToCache(VendorDataService.baseUrl);
    }
    fetchVendors(status) {
        const param = status ? `?vendorStatus=${status}` : '';
        return this.http.get(`${VendorDataService.baseUrl}${param}`);
    }
    fetchVendor(id) {
        return this.http.get(`${VendorDataService.baseUrl}${id}`);
    }
    createVendor(vendor) {
        return this.http.put(`${VendorDataService.baseUrl}`, vendor);
    }
    updateVendor(vendor) {
        return this.http.post(`${VendorDataService.baseUrl}${vendor.id}`, vendor);
    }
    deleteVendor(id) {
        return this.http.delete(`${VendorDataService.baseUrl}${id}`);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: VendorDataService, deps: [{ token: i1.HttpClient }, { token: CacheRegistrationService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: VendorDataService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: VendorDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }, { type: CacheRegistrationService }] });

function generateEmptyVendor() {
    return {
        id: 0,
        shortName: '',
        fullName: '',
        status: Status.Inactive,
        url: '',
    };
}
class VendorService {
    constructor(vendorDataService) {
        this.vendorDataService = vendorDataService;
    }
    fetchVendors(status) {
        return this.vendorDataService.fetchVendors(status);
    }
    fetchVendor(id) {
        return this.vendorDataService.fetchVendor(id);
    }
    saveVendor(vendor) {
        return vendor.id ?
            this.vendorDataService.updateVendor(vendor) :
            this.vendorDataService.createVendor(vendor);
    }
    deleteVendor(id) {
        return this.vendorDataService.deleteVendor(id);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: VendorService, deps: [{ token: VendorDataService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: VendorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: VendorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: VendorDataService }] });

function generateEmptyProduct() {
    return {
        id: 0,
        title: '',
        shortDescription: '',
        longDescription: '',
        status: Status.Inactive,
        featured: false,
        informationalOnly: false,
        informationalUrl: '',
        faqUrl: '',
        imageId: 0,
        vendor: generateEmptyVendor(),
        licenses: [],
    };
}
function generateEmptyProductLicense(type = LicenseType.Employee) {
    return {
        id: 0,
        productId: 0,
        type: type,
        status: Status.Inactive,
        offerings: [],
    };
}
class ProductService {
    constructor(productDataService) {
        this.productDataService = productDataService;
    }
    fetchProducts(filters) {
        return this.productDataService.fetchProducts(filters);
    }
    fetchProduct(id, licenseStatus, offeringStatus) {
        return this.productDataService.fetchProduct(id).pipe(map(product => this.filterProduct(product, licenseStatus, offeringStatus)));
    }
    findLicenseFromOffering(product, offering) {
        return product.licenses?.find(l => l.offerings?.find(o => o.id === offering.id));
    }
    findLicense(product, licenseType) {
        return product?.licenses?.find(license => license.type === licenseType);
    }
    findOffering(offerings, id) {
        return offerings.find(offering => offering.id === id);
    }
    findOfferingFromProduct(product, offeringId) {
        for (const license of product.licenses) {
            const found = license.offerings.find(offering => offering.id === offeringId);
            if (found) {
                return found;
            }
        }
        return undefined;
    }
    createProduct(product) {
        return this.productDataService.createProduct(product);
    }
    updateProduct(product) {
        return this.productDataService.updateProduct(product);
    }
    filterProduct(product, licenseStatus, offeringStatus) {
        //clone to prevent mutation of the existing product.
        let clonedProduct = cloneDeep(product);
        if (licenseStatus && product.licenses) {
            clonedProduct.licenses = clonedProduct.licenses.filter(license => licenseStatus === license.status);
        }
        if (offeringStatus) {
            clonedProduct.licenses.forEach(license => {
                if (license.offerings) {
                    license.offerings = license.offerings.filter(offering => offeringStatus === offering.status);
                }
            });
        }
        return clonedProduct;
    }
    filterEmptyLicenses(product) {
        //clone to prevent mutation of the existing product.
        let clonedProduct = cloneDeep(product);
        clonedProduct.licenses = clonedProduct.licenses.filter(li => li.offerings && li.offerings.length);
        return clonedProduct;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: ProductService, deps: [{ token: ProductDataService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: ProductService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: ProductService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: ProductDataService }] });

class ProductOperatorService {
    constructor() {
        this.notificationService = inject(NotificationService);
        this.productService = inject(ProductService);
        /**
         * Fetches a product with the given id
         * Throws an error if the fetch from the server fails
         */
        this.fetchProduct = () => {
            return pipe(switchMap(id => id === -1 ? of(generateEmptyProduct()) : this.productService.fetchProduct(id)
                .pipe(catchError$1(err => {
                if (err instanceof HttpErrorResponse && err.status === HttpStatusCode.NotFound) {
                    this.notificationService.addError(`A product with id: ${id} could not be found`);
                }
                else { /* Unexpected Error */
                    const message = Messages.validationMsg(err, 'Failed to load product. Please contact support if the problem continues');
                    this.notificationService.addError(message);
                }
                return of(generateEmptyProduct());
            }))));
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: ProductOperatorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: ProductOperatorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: ProductOperatorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class TitleSortService {
    constructor() {
    }
    sort(list) {
        return list && sortBy(list, 'title');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: TitleSortService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: TitleSortService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: TitleSortService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class IdOperatorService {
    constructor() {
        this.notificationService = inject(NotificationService);
        this.ignoreMissingId = () => {
            return pipe(filter((id) => !!id));
        };
        this.parseId = () => {
            return pipe(map((idString) => {
                const idInt = parseInt(idString, 10);
                if (!isNaN(idInt)) {
                    return idInt;
                }
                else {
                    this.notificationService.addError(`An invalid input was given: ${idString}`);
                    return -1;
                }
            }));
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: IdOperatorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: IdOperatorService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.3", ngImport: i0, type: IdOperatorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/*
 * Public API Surface of software-catalog-ng-core
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AddOffer, AddOfferings, AddProduct, AdminGuard, BehaviorSubjectService, CacheInterceptor, CacheRegistrationService, CacheService, ContractTerm, DownloadDataService, DownloadService, DownloadType, IdOperatorService, LegacyRedirectGuard, LicenseDataService, LicenseService, LicenseType, LoadUserGuard, MediaType, Messages, OfferingDataService, OfferingOperatorService, OfferingService, ProductDataService, ProductOfferingStatus, ProductOperatorService, ProductService, ScCoreModule, SkipLinkComponent, Status, TitleSortService, UpgradeProtectionType, UsageType, UserDataService, UserService, VendorDataService, VendorService, WorkAtHomeType, generateEmptyPlatform, generateEmptyProduct, generateEmptyProductLicense, generateEmptyProductOffering, generateEmptyVendor };
